/* ===========================================================
   files/folder-sync.js — a folder on this computer kept in step with
   Drive (his asks, 2026-10-28: docs and code files sync between
   computers and the cloud; no extra programs)

   Pick a folder once per computer (Edge/Chrome). While Drive is open:
     - a file you add or change in the folder uploads to Drive
     - a Drive file this folder doesn't have yet downloads into it
     - every doc is written as "<title>.md" (newest wins from the cloud)
   Runs every 30 s. Handles stay on this device (IndexedDB).
   =========================================================== */

import { docs } from '../state.js';
import { api } from '../lib/api.js';
import { createDoc, flushOutbox, refreshList } from '../sync/docs.js';
import { safeName } from './export.js';
import { toMarkdown } from '../lib/markdown.js';

const DB = 'dp-folder', STORE = 'kv';
const idb = mode => new Promise((ok, no) => {
  const r = indexedDB.open(DB, 1);
  r.onupgradeneeded = () => r.result.createObjectStore(STORE);
  r.onsuccess = () => ok(r.result.transaction(STORE, mode).objectStore(STORE));
  r.onerror = () => no(r.error);
});
const get = k => idb('readonly').then(s => new Promise(ok => { const q = s.get(k); q.onsuccess = () => ok(q.result); q.onerror = () => ok(null); }));
const put = (k, v) => idb('readwrite').then(s => s.put(v, k));

export const canSync = () => typeof window !== 'undefined' && 'showDirectoryPicker' in window;
export const syncedFolder = () => get('dir');

export async function pickFolder() {
  const dir = await window.showDirectoryPicker({ mode: 'readwrite', id: 'dp-drive' });
  await put('dir', dir);
  return dir;
}

async function allowed(dir) {
  if ((await dir.queryPermission({ mode: 'readwrite' })) === 'granted') return true;
  return (await dir.requestPermission({ mode: 'readwrite' })) === 'granted';
}

let running = false;

/** One pass. Returns { up, down, docs } counts. */
export async function syncOnce() {
  const dir = await get('dir');
  if (!dir || running || !(await allowed(dir))) return null;
  running = true;
  const seen = (await get('seen')) || {};           // file name → lastModified we last synced
  const out = { up: 0, down: 0, docs: 0 };
  try {
    const cloud = new Map(docs.value.filter(d => d.kind === 'file' && !d.deletedAt).map(d => [d.title, d]));
    const local = new Map();
    for await (const [name, h] of dir.entries()) if (h.kind === 'file' && !name.endsWith('.md')) local.set(name, h);

    // folder → Drive: new or changed files
    for (const [name, h] of local) {
      const f = await h.getFile();
      if (seen[name] === f.lastModified) continue;
      if (f.size > 20 * 1024 * 1024) continue;
      let doc = cloud.get(name);
      const id = doc ? doc.id : await createDoc({ title: name, kind: 'file' });
      if (!doc) await flushOutbox();
      const r = await api(`/api/files?doc=${id}`, { method: 'POST', raw: f, type: 'application/octet-stream' });
      await api(`/api/docs/${id}`, { method: 'PATCH', body: { snippet: r.url, words: r.bytes } });
      seen[name] = f.lastModified;
      out.up++;
    }

    // Drive → folder: files this computer doesn't have
    for (const [name, d] of cloud) {
      if (local.has(name) || !d.snippet?.startsWith('/f/')) continue;
      const res = await fetch(d.snippet, { credentials: 'same-origin' });
      if (!res.ok) continue;
      const fh = await dir.getFileHandle(safeName(name), { create: true });
      const w = await fh.createWritable();
      await w.write(await res.blob());
      await w.close();
      seen[safeName(name)] = (await fh.getFile()).lastModified;
      out.down++;
    }

    // docs → "<title>.md" (one way: the cloud copy is the real one)
    const written = (await get('docs')) || {};
    for (const d of docs.value.filter(x => (x.kind === 'doc' || x.kind === 'copy') && !x.deletedAt && x.role === 'owner')) {
      if (written[d.id] === d.updatedAt) continue;
      try {
        const { ydoc } = await import('../sync/docs.js').then(m => m.loadForExport(d.id));
        const { allTabsJSON } = await import('../editor/convert.js');
        const fh = await dir.getFileHandle(`${safeName(d.title || 'Untitled document')}.md`, { create: true });
        const w = await fh.createWritable();
        await w.write(toMarkdown(allTabsJSON(ydoc)));
        await w.close();
        written[d.id] = d.updatedAt;
        out.docs++;
      } catch { /* not on this device yet: next pass */ }
    }
    await put('docs', written);
    await put('seen', seen);
    if (out.up) refreshList();
  } finally { running = false; }
  return out;
}
